import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LogOut, BarChart3, Users, FileText, Activity, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_attorney-ready/artifacts/06pkbseh_lexintake%20logo.png";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const links = [
    { to: "/admin/dashboard", label: "Overview", icon: BarChart3, testId: "nav-admin-overview" },
    { to: "/admin/users", label: "Users", icon: Users, testId: "nav-admin-users" },
    { to: "/admin/cases", label: "Cases", icon: FileText, testId: "nav-admin-cases" },
    { to: "/admin/billing", label: "Billing", icon: DollarSign, testId: "nav-admin-billing" },
    { to: "/admin/audit", label: "Audit log", icon: Activity, testId: "nav-admin-audit" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden md:flex flex-col w-64 bg-[#0A192F] text-white">
        <div className="p-6 border-b border-white/10">
          <img src={LOGO_URL} alt="LexIntake" className="h-12 w-auto object-contain bg-white rounded-md px-2 py-1" />
          <p className="mt-3 text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059]">
            Admin Console
          </p>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end
              data-testid={l.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? "bg-white/10 text-[#C5A059]"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <p className="px-3 text-sm font-medium text-white">{user?.name}</p>
          <p className="px-3 text-xs text-slate-400 mb-2">{user?.email}</p>
          <Button
            variant="ghost"
            size="sm"
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="w-full justify-start text-slate-300 hover:bg-white/5 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="md:hidden bg-[#0A192F] text-white">
          <div className="px-6 py-4 flex items-center justify-between">
            <p className="font-heading text-lg font-semibold">LexIntake Admin</p>
            <Button
              size="sm"
              variant="ghost"
              data-testid="admin-mobile-logout-btn"
              onClick={handleLogout}
              className="text-slate-300"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <nav className="px-6 pb-3 flex gap-3 overflow-x-auto">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                className={({ isActive }) =>
                  `text-xs whitespace-nowrap px-3 py-1.5 rounded-full ${
                    isActive ? "bg-[#C5A059] text-[#0A192F]" : "bg-white/10 text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
